import { useEffect, useMemo, useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Alert,
  App as AntdApp,
  Button,
  Card,
  Col,
  Divider,
  Drawer,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import {
  AndroidOutlined,
  EditOutlined,
  InfoCircleOutlined,
  ReloadOutlined,
  SaveOutlined,
} from '@ant-design/icons'
import {
  ANDROID_AD_CHANNELS,
  ANDROID_AD_FORMATS,
  ANDROID_AD_PLACEMENT_CODES,
  formatAndroidAdError,
  getAndroidAdConfig,
  setAndroidAdRuntimeSettings,
  upsertAndroidAdPlacement,
} from '@/lib/android-ads'
import type {
  AndroidAdChannel,
  AndroidAdFormat,
  AndroidAdPlacement,
  AndroidAdPlacementCode,
  AndroidAdRuntimeSettings,
} from '@/lib/android-ads'
import { formatCST } from '@/lib/time'
import { useIsMobile } from '@/lib/hooks'
import { AdminMobileList } from '@/components/AdminMobileList'

export const Route = createFileRoute('/_authed/android-ads')({
  component: AndroidAdsPage,
})

interface SettingsFormValues {
  adsEnabled: boolean
  globalCooldownSeconds: number
  dailyCap: number
  reason: string
}

interface PlacementFormValues {
  placementCode: AndroidAdPlacementCode
  format: AndroidAdFormat
  vendor: string
  enabled: boolean
  channels: AndroidAdChannel[]
  minVersionCode: number | null
  maxVersionCode: number | null
  rewardHours: number | null
  cooldownSeconds: number
  sessionCap: number
  dailyCap: number | null
  loadTimeoutSeconds: number
  reason: string
}

const PLACEMENT_LABELS: Record<AndroidAdPlacementCode, string> = {
  player_rewarded_ad_free: '播放器激励免广告',
  player_interstitial_break: '播放器插屏',
  home_interstitial: '首页插屏',
  search_interstitial: '搜索页插屏',
  profile_interstitial: '个人中心插屏',
}

const CHANNEL_LABELS: Record<AndroidAdChannel, string> = {
  release: '正式版',
  beta: 'Beta',
  special: '定向版',
}

const CHANNEL_COLORS: Record<AndroidAdChannel, string> = {
  release: 'blue',
  beta: 'purple',
  special: 'cyan',
}

const REASON_RULES = [
  { required: true, message: '请输入修改原因' },
  {
    validator: (_: unknown, value?: string) => {
      const length = value?.trim().length ?? 0
      return length >= 3 && length <= 1000
        ? Promise.resolve()
        : Promise.reject(new Error('修改原因必须为 3–1000 个字符'))
    },
  },
]

function AndroidAdsPage() {
  const queryClient = useQueryClient()
  const { message } = AntdApp.useApp()
  const [editing, setEditing] = useState<AndroidAdPlacement | null>(null)
  const isMobile = useIsMobile()

  const configQuery = useQuery({
    queryKey: ['android-ad-config'],
    queryFn: getAndroidAdConfig,
  })

  const settingsMutation = useMutation({
    mutationFn: setAndroidAdRuntimeSettings,
    onSuccess: async () => {
      message.success('全局广告设置已保存并立即生效')
      await queryClient.invalidateQueries({ queryKey: ['android-ad-config'] })
    },
    onError: (error) =>
      message.error(`保存失败：${formatAndroidAdError(error)}`),
  })

  const placementMutation = useMutation({
    mutationFn: upsertAndroidAdPlacement,
    onSuccess: async () => {
      message.success('Placement 已保存并立即生效')
      setEditing(null)
      await queryClient.invalidateQueries({ queryKey: ['android-ad-config'] })
    },
    onError: (error) =>
      message.error(`保存失败：${formatAndroidAdError(error)}`),
  })

  const placements = useMemo(() => {
    const rows = configQuery.data?.placements ?? []
    return [...rows].sort(
      (a, b) =>
        ANDROID_AD_PLACEMENT_CODES.indexOf(a.placement_code) -
        ANDROID_AD_PLACEMENT_CODES.indexOf(b.placement_code),
    )
  }, [configQuery.data?.placements])

  const columns: ColumnsType<AndroidAdPlacement> = useMemo(
    () => [
      {
        title: 'Placement',
        dataIndex: 'placement_code',
        width: 230,
        render: (code: AndroidAdPlacementCode) => (
          <Space direction="vertical" size={0}>
            <Typography.Text strong>{PLACEMENT_LABELS[code]}</Typography.Text>
            <Typography.Text type="secondary" className="app-update-mono">
              {code}
            </Typography.Text>
          </Space>
        ),
      },
      {
        title: '状态',
        dataIndex: 'enabled',
        width: 80,
        render: (enabled: boolean) =>
          enabled ? <Tag color="success">启用</Tag> : <Tag>停用</Tag>,
      },
      {
        title: '格式',
        dataIndex: 'format',
        width: 100,
        render: (format: AndroidAdFormat) => (
          <Tag color={format === 'rewarded' ? 'blue' : 'geekblue'}>
            {format === 'rewarded' ? '激励广告' : '插屏广告'}
          </Tag>
        ),
      },
      {
        title: 'Vendor',
        dataIndex: 'vendor',
        width: 110,
        render: (vendor: string) => (
          <Typography.Text className="app-update-mono">
            {vendor}
          </Typography.Text>
        ),
      },
      {
        title: '渠道',
        dataIndex: 'channels',
        width: 190,
        render: (channels: AndroidAdChannel[]) => (
          <Space size={[0, 4]} wrap>
            {channels.map((channel) => (
              <Tag key={channel} color={CHANNEL_COLORS[channel]}>
                {CHANNEL_LABELS[channel]}
              </Tag>
            ))}
          </Space>
        ),
      },
      {
        title: '版本范围',
        width: 140,
        render: (_: unknown, row) => (
          <Typography.Text className="app-update-mono">
            {formatVersionRange(row.min_version_code, row.max_version_code)}
          </Typography.Text>
        ),
      },
      {
        title: '奖励',
        width: 100,
        render: (_: unknown, row) =>
          row.format === 'rewarded' ? (
            <Tooltip title={row.reward_kind ?? undefined}>
              <Typography.Text>{row.reward_value ?? '—'} 小时</Typography.Text>
            </Tooltip>
          ) : (
            '—'
          ),
      },
      {
        title: '冷却',
        dataIndex: 'cooldown_seconds',
        width: 90,
        render: (seconds: number) => (
          <Typography.Text className="app-update-mono">
            {formatSeconds(seconds)}
          </Typography.Text>
        ),
      },
      {
        title: 'Session / 每日',
        width: 120,
        render: (_: unknown, row) => (
          <Typography.Text className="app-update-mono">
            {row.session_cap} / {row.daily_cap ?? '不限'}
          </Typography.Text>
        ),
      },
      {
        title: '加载超时',
        dataIndex: 'load_timeout_seconds',
        width: 90,
        render: (seconds: number) => (
          <Typography.Text className="app-update-mono">
            {seconds} 秒
          </Typography.Text>
        ),
      },
      {
        title: '更新于',
        dataIndex: 'updated_at',
        width: 150,
        render: (value: string) => formatCST(value, 'YYYY-MM-DD HH:mm'),
      },
      {
        title: '操作',
        width: 76,
        fixed: 'right',
        render: (_: unknown, row) => (
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => setEditing(row)}
          >
            编辑
          </Button>
        ),
      },
    ],
    [],
  )

  const settings = configQuery.data?.settings

  return (
    <div className="admin-page android-ad-page">
      <Card loading={configQuery.isLoading}>
        <div className="app-update-heading">
          <div>
            <Space size={8} wrap>
              <AndroidOutlined />
              <Typography.Title level={4} style={{ margin: 0 }}>
                Android 广告
              </Typography.Title>
              <Tag color="green">Android SDK</Tag>
              <Typography.Text
                type="secondary"
                className="app-update-mono admin-desktop-only"
              >
                admin.get_android_ad_config
              </Typography.Text>
            </Space>
            <Typography.Text type="secondary" className="android-ad-subtitle">
              管理 Android SDK 的全局开关、频控与 Placement 策略
            </Typography.Text>
          </div>
          {isMobile ? (
            <Tooltip title="刷新">
              <Button
                icon={<ReloadOutlined />}
                aria-label="刷新"
                loading={configQuery.isFetching}
                onClick={() => void configQuery.refetch()}
              />
            </Tooltip>
          ) : (
            <Button
              icon={<ReloadOutlined />}
              loading={configQuery.isFetching}
              onClick={() => void configQuery.refetch()}
            >
              刷新
            </Button>
          )}
        </div>

        {configQuery.isError ? (
          <Alert
            type="error"
            showIcon
            message="Android 广告配置加载失败"
            description={formatAndroidAdError(configQuery.error)}
            action={
              <Button size="small" onClick={() => void configQuery.refetch()}>
                重新加载
              </Button>
            }
          />
        ) : settings ? (
          <>
            <SettingsForm
              settings={settings}
              saving={settingsMutation.isPending}
              onSave={(values) => settingsMutation.mutate(values)}
            />

            <Divider />

            <div className="android-ad-section-heading">
              <Space size={8}>
                <Typography.Title level={5} style={{ margin: 0 }}>
                  Placement
                </Typography.Title>
                <Tag>{placements.length}</Tag>
              </Space>
              <Typography.Text type="secondary">
                每次修改均写入 moderation.logs；配置保存后立即生效
              </Typography.Text>
            </div>

            <div className="admin-table-wrap">
              <Table<AndroidAdPlacement>
                rowKey="placement_code"
                columns={columns}
                dataSource={placements}
                size="middle"
                pagination={false}
                scroll={{ x: 1480 }}
                locale={{ emptyText: '暂无 Android 广告 Placement' }}
              />
            </div>
            <AdminMobileList
              rows={placements}
              rowKey={(row) => row.placement_code}
              loading={configQuery.isFetching}
              renderItem={(row) => (
                <article
                  className="admin-mobile-record"
                  role="button"
                  tabIndex={0}
                  aria-label={`编辑 ${PLACEMENT_LABELS[row.placement_code]}`}
                  onClick={() => setEditing(row)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') setEditing(row)
                  }}
                >
                  <div className="admin-mobile-record__head">
                    <div className="admin-mobile-record__title">
                      {PLACEMENT_LABELS[row.placement_code]}
                      <div className="admin-mobile-record__meta">
                        <span className="app-update-mono">
                          {row.placement_code}
                        </span>
                      </div>
                    </div>
                    {row.enabled ? (
                      <Tag color="success">启用</Tag>
                    ) : (
                      <Tag>停用</Tag>
                    )}
                  </div>
                  <div className="admin-mobile-record__badges">
                    <Tag
                      color={row.format === 'rewarded' ? 'blue' : 'geekblue'}
                    >
                      {row.format === 'rewarded' ? '激励广告' : '插屏广告'}
                    </Tag>
                    {row.channels.map((channel) => (
                      <Tag key={channel} color={CHANNEL_COLORS[channel]}>
                        {CHANNEL_LABELS[channel]}
                      </Tag>
                    ))}
                  </div>
                  <div className="admin-mobile-record__meta">
                    <span className="app-update-mono">{row.vendor}</span>
                    <span>
                      版本{' '}
                      {formatVersionRange(
                        row.min_version_code,
                        row.max_version_code,
                      )}
                    </span>
                    {row.format === 'rewarded' ? (
                      <span>奖励 {row.reward_value ?? '—'} 小时</span>
                    ) : null}
                  </div>
                  <div className="admin-mobile-record__meta">
                    <span>冷却 {formatSeconds(row.cooldown_seconds)}</span>
                    <span>
                      Session/每日 {row.session_cap} / {row.daily_cap ?? '不限'}
                    </span>
                    <span>超时 {row.load_timeout_seconds} 秒</span>
                    <span>更新 {formatCST(row.updated_at, 'MM-DD HH:mm')}</span>
                  </div>
                </article>
              )}
            />
          </>
        ) : null}
      </Card>

      <PlacementDrawer
        placement={editing}
        saving={placementMutation.isPending}
        onClose={() => setEditing(null)}
        onSave={(values) => placementMutation.mutate(values)}
      />
    </div>
  )
}

function SettingsForm({
  settings,
  saving,
  onSave,
}: {
  settings: AndroidAdRuntimeSettings
  saving: boolean
  onSave: (values: SettingsFormValues) => void
}) {
  const [form] = Form.useForm<SettingsFormValues>()
  const isMobile = useIsMobile()

  useEffect(() => {
    form.setFieldsValue({
      adsEnabled: settings.ads_enabled,
      globalCooldownSeconds: settings.interstitial_global_cooldown_seconds,
      dailyCap: settings.interstitial_daily_cap,
      reason: '',
    })
  }, [form, settings])

  return (
    <section aria-labelledby="android-ad-settings-title">
      <div className="android-ad-section-heading">
        <Space size={8} wrap>
          <Typography.Title
            id="android-ad-settings-title"
            level={5}
            style={{ margin: 0 }}
          >
            全局设置
          </Typography.Title>
          <Tag color={settings.ads_enabled ? 'success' : 'default'}>
            {settings.ads_enabled ? '广告总开关已启用' : '广告总开关已停用'}
          </Tag>
        </Space>
        <Typography.Text type="secondary">
          更新于 {formatCST(settings.updated_at)}
        </Typography.Text>
      </div>

      <Form<SettingsFormValues>
        form={form}
        layout="vertical"
        onFinish={onSave}
        disabled={saving}
      >
        <Row gutter={16} align="bottom">
          <Col xs={24} md={8} xl={4}>
            <Form.Item
              name="adsEnabled"
              label="广告总开关"
              valuePropName="checked"
            >
              <Switch checkedChildren="启用" unCheckedChildren="停用" />
            </Form.Item>
          </Col>
          <Col xs={12} md={8} xl={5}>
            <Form.Item
              name="globalCooldownSeconds"
              label="插屏全局冷却（秒）"
              rules={[{ required: true, type: 'number', min: 0, max: 86400 }]}
            >
              <InputNumber
                min={0}
                max={86400}
                precision={0}
                style={{ width: '100%' }}
              />
            </Form.Item>
          </Col>
          <Col xs={12} md={8} xl={4}>
            <Form.Item
              name="dailyCap"
              label="插屏每日上限"
              rules={[{ required: true, type: 'number', min: 0, max: 100 }]}
            >
              <InputNumber
                min={0}
                max={100}
                precision={0}
                addonAfter="次"
                style={{ width: '100%' }}
              />
            </Form.Item>
          </Col>
          <Col xs={24} xl={8}>
            <Form.Item name="reason" label="修改原因" rules={REASON_RULES}>
              <Input.TextArea
                rows={2}
                maxLength={1000}
                showCount
                placeholder="必填，说明本次调整原因"
              />
            </Form.Item>
          </Col>
          <Col xs={24} xl={3}>
            {/* 桌面上「保存」标签只为与左侧控件底部对齐；手机纵向排列时不需要 */}
            <Form.Item label={isMobile ? undefined : '保存'}>
              <Button
                type="primary"
                htmlType="submit"
                icon={<SaveOutlined />}
                loading={saving}
                block
              >
                保存设置
              </Button>
            </Form.Item>
          </Col>
        </Row>
      </Form>

      <Alert
        type="info"
        showIcon
        icon={<InfoCircleOutlined />}
        message="总开关与插屏全局频控对所有 Placement 生效；保存后立即应用，并记录审计日志。"
      />
    </section>
  )
}

function PlacementDrawer({
  placement,
  saving,
  onClose,
  onSave,
}: {
  placement: AndroidAdPlacement | null
  saving: boolean
  onClose: () => void
  onSave: (values: PlacementFormValues) => void
}) {
  const [form] = Form.useForm<PlacementFormValues>()
  const isMobile = useIsMobile()

  useEffect(() => {
    if (!placement) return
    form.setFieldsValue({
      placementCode: placement.placement_code,
      format: placement.format,
      vendor: placement.vendor,
      enabled: placement.enabled,
      channels: placement.channels,
      minVersionCode: placement.min_version_code,
      maxVersionCode: placement.max_version_code,
      rewardHours:
        placement.format === 'rewarded' ? placement.reward_value : null,
      cooldownSeconds: placement.cooldown_seconds,
      sessionCap: placement.session_cap,
      dailyCap: placement.daily_cap,
      loadTimeoutSeconds: placement.load_timeout_seconds,
      reason: '',
    })
  }, [form, placement])

  return (
    <Drawer
      title={
        <Space direction="vertical" size={0}>
          <span>编辑 Placement</span>
          <Typography.Text type="secondary" className="app-update-mono">
            {placement?.placement_code}
          </Typography.Text>
        </Space>
      }
      open={placement !== null}
      width={isMobile ? '100%' : 720}
      destroyOnHidden
      maskClosable={!saving}
      closable={!saving}
      onClose={onClose}
      footer={
        <Space style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button disabled={saving} onClick={onClose}>
            放弃修改
          </Button>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            loading={saving}
            onClick={() => form.submit()}
          >
            保存 Placement
          </Button>
        </Space>
      }
    >
      <Form<PlacementFormValues>
        form={form}
        layout="vertical"
        disabled={saving}
        onFinish={onSave}
      >
        <Form.Item name="placementCode" label="Placement code">
          <Input disabled className="app-update-mono" />
        </Form.Item>

        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item
              name="format"
              label="广告格式"
              extra="由 placement_code 的数据库约束决定"
              rules={[{ required: true, message: '请选择广告格式' }]}
            >
              <Select
                disabled
                options={ANDROID_AD_FORMATS.map((format) => ({
                  value: format,
                  label: format === 'rewarded' ? '激励广告' : '插屏广告',
                }))}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              name="vendor"
              label="Vendor"
              rules={[
                { required: true, whitespace: true, message: '请输入 Vendor' },
                {
                  pattern: /^[a-z][a-z0-9_-]{0,63}$/i,
                  message: '只能使用字母开头的字母、数字、下划线或连字符',
                },
              ]}
            >
              <Input placeholder="例如 gromore" />
            </Form.Item>
          </Col>
        </Row>

        <Space size="large" style={{ marginBottom: 16 }} wrap>
          <Form.Item
            name="enabled"
            label="Placement 状态"
            valuePropName="checked"
            style={{ marginBottom: 0 }}
          >
            <Switch checkedChildren="启用" unCheckedChildren="停用" />
          </Form.Item>
        </Space>

        <Form.Item
          name="channels"
          label="投放渠道"
          rules={[
            {
              required: true,
              type: 'array',
              min: 1,
              message: '至少选择一个渠道',
            },
          ]}
        >
          <Select
            mode="multiple"
            options={ANDROID_AD_CHANNELS.map((channel) => ({
              value: channel,
              label: `${CHANNEL_LABELS[channel]} (${channel})`,
            }))}
          />
        </Form.Item>

        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item
              name="minVersionCode"
              label="最低 version_code"
              rules={[{ type: 'number', min: 1 }]}
            >
              <InputNumber
                min={1}
                precision={0}
                placeholder="为空表示不限制"
                style={{ width: '100%' }}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              name="maxVersionCode"
              label="最高 version_code"
              dependencies={['minVersionCode']}
              rules={[
                { type: 'number', min: 1 },
                ({ getFieldValue }) => ({
                  validator(_, value: number | null) {
                    const min = getFieldValue('minVersionCode') as number | null
                    return value == null || min == null || min <= value
                      ? Promise.resolve()
                      : Promise.reject(
                          new Error('最高 version_code 不能小于最低值'),
                        )
                  },
                }),
              ]}
            >
              <InputNumber
                min={1}
                precision={0}
                placeholder="为空表示不限制"
                style={{ width: '100%' }}
              />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          noStyle
          shouldUpdate={(prev, next) => prev.format !== next.format}
        >
          {({ getFieldValue }) =>
            getFieldValue('format') === 'rewarded' ? (
              <Form.Item
                name="rewardHours"
                label="激励免广告时长"
                extra="固定发放 interstitial_and_playback_ad_free，单位为小时"
                rules={[{ required: true, type: 'number', min: 1, max: 24 }]}
              >
                <InputNumber
                  min={1}
                  max={24}
                  precision={0}
                  addonAfter="小时"
                  style={{ width: '100%' }}
                />
              </Form.Item>
            ) : (
              <Alert
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
                message="插屏广告保存时会将 reward_kind、reward_value、reward_unit 清空。"
              />
            )
          }
        </Form.Item>

        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item
              name="cooldownSeconds"
              label="Placement 冷却（秒）"
              rules={[{ required: true, type: 'number', min: 0, max: 86400 }]}
            >
              <InputNumber
                min={0}
                max={86400}
                precision={0}
                style={{ width: '100%' }}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              name="sessionCap"
              label="Session 上限"
              rules={[{ required: true, type: 'number', min: 0, max: 10 }]}
            >
              <InputNumber
                min={0}
                max={10}
                precision={0}
                addonAfter="次"
                style={{ width: '100%' }}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              name="dailyCap"
              label="Placement 每日上限"
              extra="为空表示不限制"
              rules={[{ type: 'number', min: 0, max: 100 }]}
            >
              <InputNumber
                min={0}
                max={100}
                precision={0}
                addonAfter="次"
                style={{ width: '100%' }}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              name="loadTimeoutSeconds"
              label="加载超时（秒）"
              rules={[{ required: true, type: 'number', min: 1, max: 60 }]}
            >
              <InputNumber
                min={1}
                max={60}
                precision={0}
                style={{ width: '100%' }}
              />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item name="reason" label="修改原因" rules={REASON_RULES}>
          <Input.TextArea
            rows={3}
            maxLength={1000}
            showCount
            placeholder="必填，说明本次 Placement 调整原因"
          />
        </Form.Item>
      </Form>
    </Drawer>
  )
}

function formatVersionRange(min: number | null, max: number | null): string {
  if (min === null && max === null) return '不限'
  if (min === null) return `≤ ${max}`
  if (max === null) return `≥ ${min}`
  return `${min}–${max}`
}

function formatSeconds(seconds: number): string {
  if (seconds === 0) return '0 秒'
  if (seconds % 3600 === 0) return `${seconds / 3600} 小时`
  if (seconds % 60 === 0) return `${seconds / 60} 分钟`
  return `${seconds} 秒`
}
